import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';

import { getEmployeeInfo } from '@entities/employee/api/employees';

/**
 * useEmployeeQueries 훅
 * - 결재라인에 있는 사원 정보 조회
 * - 참조자/시행자 직원 정보 조회
 */
function useEmployeeQueries({ docData, companyCode, receiverListData, executorListData }) {
  // 결재라인에 있는 사원번호들 추출 (중복 제거)
  // 기안자는 sign_1, sign_2~5는 결재자, check_1~4는 검토자, agreement는 합의자
  const approvalLineEnumbers = useMemo(
    function () {
      if (!docData) return [];
      const enumbers = [
        // 기안자: sign_1
        docData.sign_1,
        // 결재자 1~4번 (sign_2~5)
        docData.sign_2,
        docData.sign_3,
        docData.sign_4,
        docData.sign_5,
        // 검토자 1~4번 (check_1~4)
        docData.check_1,
        docData.check_2,
        docData.check_3,
        docData.check_4,
        // 합의자
        docData.agreement,
      ]
        .filter((enumber) => enumber != null && enumber !== undefined && enumber !== 0)
        .map((enumber) => Number(enumber));

      // 중복 제거 후 정렬하여 안정성 확보
      return [...new Set(enumbers)].sort((a, b) => a - b);
    },
    [
      docData?.sign_1,
      docData?.sign_2,
      docData?.sign_3,
      docData?.sign_4,
      docData?.sign_5,
      docData?.check_1,
      docData?.check_2,
      docData?.check_3,
      docData?.check_4,
      docData?.agreement,
    ],
  );

  // useQueries를 위한 쿼리 배열 안정화
  const employeeQueriesConfig = useMemo(
    function () {
      if (!approvalLineEnumbers.length || !companyCode || !docData) {
        return [];
      }
      return approvalLineEnumbers.map((enumber) => ({
        queryKey: ['employeeInfo', enumber],
        queryFn: () => getEmployeeInfo(enumber),
        enabled: !!companyCode && !!docData && !!enumber,
      }));
    },
    [approvalLineEnumbers, companyCode, docData],
  );

  // 결재라인에 있는 사원번호들만 개별 조회
  const employeeQueries = useQueries({
    queries: employeeQueriesConfig,
  });

  // 각 쿼리의 데이터를 추출하여 안정적인 의존성 생성
  const employeeDataArray = useMemo(
    function () {
      return employeeQueries.map((query) => query.data?.resultData);
    },
    [
      employeeQueries
        .map((q, i) => `${i}-${q.data?.resultData?.name || 'loading'}-${q.isSuccess}`)
        .join('|'),
    ],
  );

  // 조회된 사원 정보를 Map으로 변환 (enumber를 키로 사용)
  const employeeMap = useMemo(
    function () {
      const map = new Map();
      employeeDataArray.forEach((employee, index) => {
        if (employee && index < approvalLineEnumbers.length) {
          const enumber = approvalLineEnumbers[index];
          map.set(enumber, {
            enumber: enumber,
            name: employee.name || '',
            position: employee.position || '',
            team: employee.team || '',
          });
        }
      });
      return map;
    },
    [employeeDataArray, approvalLineEnumbers.join(',')],
  );

  // 참조자/시행자의 enumber 목록 추출
  const recipientEnumbers = useMemo(
    function () {
      if (!receiverListData || !Array.isArray(receiverListData)) return [];
      return receiverListData
        .filter((r) => r != null && r.enumber != null)
        .map((r) => Number(r.enumber));
    },
    [receiverListData],
  );

  const executorEnumbers = useMemo(
    function () {
      if (!executorListData || !Array.isArray(executorListData)) return [];
      return executorListData
        .filter((e) => e != null && e.enumber != null)
        .map((e) => Number(e.enumber));
    },
    [executorListData],
  );

  // 참조자 직원 정보 조회
  const recipientEmployeeQueriesConfig = useMemo(
    function () {
      if (!recipientEnumbers.length || !companyCode) return [];
      return recipientEnumbers.map((enumber) => ({
        queryKey: ['recipientEmployeeInfo', enumber],
        queryFn: () => getEmployeeInfo(enumber),
        enabled: !!companyCode && !!enumber,
      }));
    },
    [recipientEnumbers, companyCode],
  );

  const recipientEmployeeQueries = useQueries({
    queries: recipientEmployeeQueriesConfig,
  });

  // 시행자 직원 정보 조회
  const executorEmployeeQueriesConfig = useMemo(
    function () {
      if (!executorEnumbers.length || !companyCode) return [];
      return executorEnumbers.map((enumber) => ({
        queryKey: ['executorEmployeeInfo', enumber],
        queryFn: () => getEmployeeInfo(enumber),
        enabled: !!companyCode && !!enumber,
      }));
    },
    [executorEnumbers, companyCode],
  );

  const executorEmployeeQueries = useQueries({
    queries: executorEmployeeQueriesConfig,
  });

  // 참조자 직원 정보 Map 생성
  const recipientEmployeeMap = useMemo(
    function () {
      const map = new Map();
      recipientEmployeeQueries.forEach((query, index) => {
        if (query.data?.resultData && index < recipientEnumbers.length) {
          const enumber = recipientEnumbers[index];
          const employee = query.data.resultData;
          map.set(enumber, {
            enumber: enumber,
            name: employee.name || '',
            position: employee.position || '',
            team: employee.team || '',
          });
        }
      });
      return map;
    },
    [recipientEmployeeQueries, recipientEnumbers.join(',')],
  );

  // 시행자 직원 정보 Map 생성
  const executorEmployeeMap = useMemo(
    function () {
      const map = new Map();
      executorEmployeeQueries.forEach((query, index) => {
        if (query.data?.resultData && index < executorEnumbers.length) {
          const enumber = executorEnumbers[index];
          const employee = query.data.resultData;
          map.set(enumber, {
            enumber: enumber,
            name: employee.name || '',
            position: employee.position || '',
            team: employee.team || '',
          });
        }
      });
      return map;
    },
    [executorEmployeeQueries, executorEnumbers.join(',')],
  );

  return {
    employeeMap,
    recipientEmployeeMap,
    executorEmployeeMap,
  };
}

export default useEmployeeQueries;
